import { SemverSchema } from './version.js';
import { z } from 'zod';
import { ModpackIdentitySchema, LauncherPackFileSchema, PackOverridesManifestSchema } from './modpack.js';
import { AddonSourceNameSchema, SideSupportSchema, ManualModSchema } from './mods.js';
import { RoleSchema } from './web.js';
export const RemovedServersSchema = z.object({
  removed: z.array(z.object({ id: z.string(), removedAt: z.iso.datetime({ offset: true }) })),
});
export type RemovedServers = z.infer<typeof RemovedServersSchema>;
export const LauncherUpdateFileSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*\.(yml|exe|blockmap|dmg)$/)
  .refine((filename) => !filename.includes('..'));
export const LauncherRepoSchema = z
  .string()
  .regex(/^[A-Za-z0-9][\w-]*\/[A-Za-z0-9][\w.-]*$/)
  .refine((repo) => !repo.includes('..'));
export const LauncherReleaseSchema = z.object({
  assets: z.array(
    z.object({
      id: z.number().int().positive(),
      name: z.string(),
      /** GitHub が記録した中身の sha256（"sha256:<64桁>"）。LAN から受け取ったファイルを照らすのに使う */
      digest: z.string().nullable().optional(),
    }),
  ),
  body: z
    .string()
    .max(512 * 1024)
    .nullable()
    .optional(),
});
export const LauncherReleaseNotesSchema = z.object({
  version: SemverSchema,
  body: z
    .string()
    .max(512 * 1024)
    .nullable(),
});
export const LauncherMetadataSchema = z.object({
  version: SemverSchema,
  /** Windows はインストーラー（exe）、Mac はディスクイメージ（dmg） */
  path: LauncherUpdateFileSchema.refine((name) => name.endsWith('.exe') || name.endsWith('.dmg')),
  sha512: z.string().regex(/^[A-Za-z0-9+/]{86}==$/),
  size: z.number().int().nonnegative().optional(),
  releaseDate: z.iso.datetime({ offset: true }).optional(),
});
export const LauncherPolicySchema = z
  .object({
    mode: z.enum(['off', 'warn', 'block']),
    allowExtraMods: z.boolean(),
    allowedExtraPatterns: z.array(z.string().regex(/^[a-z0-9._-]{2,40}$/)).max(50),
    recommendedMemoryGb: z.number().int().min(1).max(64).optional(),
  })
  .strict();
export type LauncherPolicy = z.infer<typeof LauncherPolicySchema>;
export const defaultLauncherPolicy = (): LauncherPolicy => ({
  mode: 'warn',
  allowExtraMods: false,
  allowedExtraPatterns: [],
});
export const ModSideInputSchema = z
  .object({
    source: AddonSourceNameSchema,
    projectId: z.string().min(1).max(100),
    clientSide: SideSupportSchema.exclude(['unknown']).nullable(),
  })
  .strict();
export const LinkIdSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
export const LinkInputSchema = z.object({ linkId: LinkIdSchema }).strict();
export const LinkVerifySchema = LinkInputSchema.extend({ username: z.string().regex(/^[A-Za-z0-9_]{3,16}$/) });
// Discord が将来ほかの項目を足しても失敗しないよう、余分な項目は無視する
export const LinkCallbackSchema = z.object({ code: z.string().min(1).max(2048), state: LinkIdSchema });
export const MinecraftIdentitySchema = z.object({
  id: z.string().regex(/^[0-9a-fA-F]{32}$/),
  name: z.string().regex(/^[A-Za-z0-9_]{3,16}$/),
});
export const DiscordIdentitySchema = z.object({
  id: z.string().regex(/^[0-9]+$/),
  username: z.string().min(1).max(100),
  global_name: z.string().max(100).nullable(),
});
export const DiscordLinkSchema = z.object({
  uuid: z.string(),
  minecraftName: z.string(),
  discordId: z.string(),
  username: z.string(),
  globalName: z.string().nullable(),
  linkedAt: z.string(),
});
export type DiscordLink = z.infer<typeof DiscordLinkSchema>;

export const DiscordUuidParamsSchema = z
  .object({ uuid: MinecraftIdentitySchema.shape.id.transform((id) => id.toLowerCase()) })
  .strict();
export const LinkStartSchema = z.object({
  linkId: LinkIdSchema,
  serverId: z.string().regex(/^[0-9a-f]{1,20}$/),
  expiresAt: z.string(),
});
export const LinkStatusSchema = z.object({
  state: z.enum(['pending', 'linked', 'expired']),
  discord: z.object({ id: z.string(), name: z.string() }).optional(),
});
export const LinkVerificationSchema = z.object({
  authorizeUrl: z.string().url(),
  current: z.object({ discordName: z.string() }).nullable(),
});
/** GET /packs/me: ランチャーがパネルにログインしている利用者。minecraftAccounts は利用者タブで登録したもの */
export const LauncherMeSchema = z.object({
  name: z.string(),
  role: RoleSchema,
  admin: z.boolean(),
  minecraftAccounts: z.array(z.object({ uuid: z.string(), name: z.string() })),
});
export type LauncherMe = z.infer<typeof LauncherMeSchema>;
export const LauncherManifestSchema = z
  .object({
    minLauncherVersion: SemverSchema.optional(),
    formatVersion: z.literal(1),
    modpack: ModpackIdentitySchema.optional(),
    files: z.array(LauncherPackFileSchema).optional(),
    overrides: PackOverridesManifestSchema.optional(),
    id: z.string(),
    name: z.string(),
    revision: z.string(),
    mcVersion: z.string(),
    loader: z.enum(['fabric', 'forge', 'neoforge', 'vanilla']),
    loaderVersion: z.string().optional(),
    unavailable: z.array(z.object({ title: z.string(), reason: z.string().optional() })).optional(),
    javaMajor: z.number().int().min(8).max(99).optional(),
    recommendedMemoryGb: z.number().int().min(1).max(64).optional(),
    server: z.object({ host: z.string(), port: z.number().int().min(1).max(65535) }),
    integrity: LauncherPolicySchema,
    mods: z.array(
      z
        .object({
          modpack: z.object({ name: z.string(), versionId: z.string(), path: z.string() }).optional(),
          source: AddonSourceNameSchema,
          projectId: z.string(),
          slug: z.string(),
          title: z.string(),
          versionNumber: z.string(),
          fileName: z.string(),
          manual: ManualModSchema.optional(),
          bundled: z.boolean().optional(),
          url: z
            .string()
            .url()
            .refine((value) => {
              const url = new URL(value);
              return (
                ((url.protocol === 'https:' ||
                  (url.protocol === 'http:' && /^\/modpack-assets\/[0-9a-f]{40}$/.test(url.pathname))) &&
                  !url.username &&
                  !url.password &&
                  [
                    'cdn.modrinth.com',
                    'edge.forgecdn.net',
                    'mediafilez.forgecdn.net',
                    'github.com',
                    'raw.githubusercontent.com',
                    'gitlab.com',
                  ].includes(url.hostname)) ||
                (/^\/(?:modpack-assets\/[0-9a-f]{40}|packs\/[^/]+\/bundled\/[0-9a-f]{40}\.jar)$/.test(url.pathname) &&
                  ['http:', 'https:'].includes(url.protocol) &&
                  !url.username &&
                  !url.password)
              );
            })
            .optional(),
          sha1: z.string().regex(/^[0-9a-f]{40}$/),
          sha512: z
            .string()
            .regex(/^[0-9a-f]{128}$/)
            .optional(),
          size: z.number().int().nonnegative(),
          side: z.enum(['required', 'optional']),
        })
        .superRefine((mod, ctx) => {
          if (mod.url && /^\/(modpack-assets|packs\/[^/]+\/bundled)\//.test(new URL(mod.url).pathname) && !mod.bundled)
            ctx.addIssue({ code: 'custom', message: 'Panel の URL は同梱 Mod 専用です' });
          if (mod.manual ? mod.url !== undefined || mod.source !== 'curseforge' : !mod.url)
            ctx.addIssue({ code: 'custom', message: 'Mod は url または CurseForge の manual の一方が必要です' });
        }),
    ),
    serverOnly: z.array(z.object({ title: z.string(), fileName: z.string() })),
  })
  .superRefine((manifest, ctx) => {
    if (manifest.loader === 'vanilla' ? manifest.loaderVersion !== undefined : !manifest.loaderVersion)
      ctx.addIssue({ code: 'custom', path: ['loaderVersion'], message: 'loaderVersion must match loader' });
  });
export type LauncherManifest = z.infer<typeof LauncherManifestSchema>;
